import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Button, ButtonModel, IconRegularClose, Spinner } from '@notino/react-styleguide'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

// hooks
import useMessages from '../../../hooks/useMessages'
import useLocalStorage from '../../../hooks/useLocalStorage'

// components
import ReservationBookingModalLayout from '../ReservationBookingModalLayout/ReservationBookingModalLayout'
import ErrorScreen from '../../../atoms/ErrorScreen/ErrorScreen'
import HookFormField from '../../../formFields/HookFormField'
import TextAreaFormField from '../../../formFields/TextAreaFormField/TextAreaFormField'
import ReservationBookingDialog from '../ReservationBookingDialog/ReservationBookingDialog'
import * as DialogSC from '../ReservationBookingDialog/ReservationBookingDialogStyles'
import CartReservationCountdown from '../CartReservationCountdown/CartReservationCountdown'
import useCartReservationProgress from '../CartReservationCountdown/useCartReservationProgress'
import InfoLine from '../../../atoms/InfoLine/InfoLine'
import { INFO_LINE_TYPE } from '../../../atoms/InfoLine/types'
import VisuallyHidden from '../../../atoms/VisuallyHidden/VisuallyHidden'

// styles
import * as SC from './Step3ReservationsSummaryStyles'

// types
import { ConfigResponse, GetCartResponse, ReservationBookingReservationsSummaryForm, ReservationBookingSelectedFormValues } from '../../../types/types'

// utils
import { AppContext } from '../../../utils/appProvider'
import { formatRelativeDate, getEmployeeName } from '../../../utils/helper'
import { FORM, LOCALE_STORAGE_KEYS, RESERVATION_BOOKING_MODAL_STEP, VALIDATION_MAX_LENGTH } from '../../../utils/enums'
import type { ReservationDeviceStorage } from '../../../utils/reservationDeviceId'
import { getCartReservationTimeSlotLabel, getUnavailableServicesModalDescription } from '../reservationBookingModalHelpers'

type Props = {
	configData: ConfigResponse
	headerImage: string | undefined
	selectedFormValues: ReservationBookingSelectedFormValues
	onBackToEmployeeAndDateSelection: (cartItem: ReservationSummaryItem) => void
	onRequestLeaveReservation: () => void
	isLeaveReservationModalOpen: boolean
	onCartReservationItemsChange: (itemCount: number) => void
	onSubmitReservationsSummaryForm: (values: ReservationBookingReservationsSummaryForm) => void
	hasAcknowledgedUnavailableServices: boolean
	onUnavailableServicesAcknowledged: () => void
	onInitNoteFromCart: (note: string) => void
	onAddAnotherService: () => void
	onCartExpired: () => void
}

type ReservationSummaryItem = GetCartResponse['cart']['cartReservationItems'][number]
type CartValidationResult = NonNullable<GetCartResponse['validationResult']>
type RemoveServiceModalState = {
	item: ReservationSummaryItem
	isRemoving: boolean
}

const RESERVATION_LIST_SKELETON_COUNT = 2

const ReservationSummaryListItemSkeleton = () => (
	<SC.ListItemSkeleton>
		<SC.ServiceImageSkeleton animated />
		<SC.ListItemSkeletonContent>
			<SC.ListItemTitleSkeleton animated />
			<SC.ListItemMetaSkeleton $width={'96%'} animated />
			<SC.ListItemMetaSkeleton $width={'46%'} animated />
			<SC.ListItemMetaSkeleton $width={'54%'} animated />
			<SC.ListItemSkeletonFooter>
				<SC.ItemPriceSkeleton animated />
				<SC.ItemEditSkeleton animated />
			</SC.ListItemSkeletonFooter>
		</SC.ListItemSkeletonContent>
	</SC.ListItemSkeleton>
)

const ReservationSummaryListSkeleton = () => (
	<SC.List>
		{Array.from({ length: RESERVATION_LIST_SKELETON_COUNT }).map((_item, index) => (
			<ReservationSummaryListItemSkeleton key={index} />
		))}
	</SC.List>
)

const Step3ReservationsSummary = (props: Props) => {
	const {
		configData: { maxCountCartReservations, cartExpiration: cartExpirationSeconds },
		headerImage,
		selectedFormValues,
		onBackToEmployeeAndDateSelection,
		onRequestLeaveReservation,
		isLeaveReservationModalOpen,
		onCartReservationItemsChange,
		onSubmitReservationsSummaryForm,
		hasAcknowledgedUnavailableServices,
		onUnavailableServicesAcknowledged,
		onInitNoteFromCart,
		onAddAnotherService,
		onCartExpired
	} = props

	const { messages } = useMessages()
	const { apiBrowser } = useContext(AppContext)
	const { formatMessage } = useIntl()

	const { storedValue: cartReservationDeviceData } = useLocalStorage<ReservationDeviceStorage>(LOCALE_STORAGE_KEYS.DEVICE_ID)
	const { cartData, expiresIn, progressState, isFetchingCart, loadingError, loadingPersistentData, fetchCart, retryFetchCart } = useCartReservationProgress()
	const [removeServiceModal, setRemoveServiceModal] = useState<RemoveServiceModalState | null>(null)
	const [cartValidationResult, setCartValidationResult] = useState<CartValidationResult | null>(null)
	const [isCartExpiredModalOpen, setIsCartExpiredModalOpen] = useState(false)
	const [isUnavailableServicesModalOpen, setIsUnavailableServicesModalOpen] = useState(false)
	const [unavailableServicesModalDescription, setUnavailableServicesModalDescription] = useState('')
	const hasInitializedNoteFromCart = useRef(false)
	const reservationItems = useMemo<ReservationSummaryItem[]>(() => cartData?.cart.cartReservationItems || [], [cartData?.cart.cartReservationItems])

	const savedNote = selectedFormValues[RESERVATION_BOOKING_MODAL_STEP.RESERVATIONS_SUMMARY].note ?? ''
	const isContentLoading = isFetchingCart || loadingPersistentData
	const validationResult = cartValidationResult ?? cartData?.validationResult

	const validationAvailabilityByItemID = useMemo(() => {
		const availabilityByItemID = new Map<string, boolean>()

		validationResult?.cartReservationItems?.forEach((validatedItem) => {
			availabilityByItemID.set(validatedItem.id, validatedItem.isAvailable)
		})

		return availabilityByItemID
	}, [validationResult?.cartReservationItems])

	const areAllReservationItemsAvailable = useMemo(() => {
		if (!reservationItems.length) {
			return false
		}

		return reservationItems.every((item) => validationAvailabilityByItemID.get(item.id) === true)
	}, [reservationItems, validationAvailabilityByItemID])
	const cartExpirationMinutes = Math.round(cartExpirationSeconds / 60)

	const {
		control,
		handleSubmit,
		formState: { isSubmitting }
	} = useForm<ReservationBookingReservationsSummaryForm>({
		defaultValues: {
			note: savedNote
		},
		values: {
			note: savedNote
		}
	})

	useEffect(() => {
		if (!cartData) {
			return
		}

		onCartReservationItemsChange(cartData.cart.cartReservationItems.length)
	}, [cartData, onCartReservationItemsChange])
	const canAddAnotherService = reservationItems.length < maxCountCartReservations

	useEffect(() => {
		if (cartData?.validationResult) {
			setCartValidationResult(cartData.validationResult)
		}
	}, [cartData?.validationResult])

	const openUnavailableServicesModal = useCallback(
		(unavailableItemIDs: string[]) => {
			if (!unavailableItemIDs.length) {
				return
			}

			setUnavailableServicesModalDescription(
				getUnavailableServicesModalDescription({
					unavailableItemIDs,
					reservationItems,
					formatMessage,
					fallbackServiceName: messages.Service
				})
			)
			setIsUnavailableServicesModalOpen(true)
		},
		[formatMessage, messages.Service, reservationItems]
	)

	useEffect(() => {
		if (
			hasAcknowledgedUnavailableServices ||
			isContentLoading ||
			loadingError ||
			!cartData ||
			isUnavailableServicesModalOpen ||
			isCartExpiredModalOpen ||
			isLeaveReservationModalOpen ||
			removeServiceModal !== null
		) {
			return
		}

		const unavailableItemIDs = validationResult?.cartReservationItems?.filter((item) => !item.isAvailable).map((item) => item.id) || []

		openUnavailableServicesModal(unavailableItemIDs)
	}, [
		cartData,
		hasAcknowledgedUnavailableServices,
		isCartExpiredModalOpen,
		isContentLoading,
		isLeaveReservationModalOpen,
		isUnavailableServicesModalOpen,
		loadingError,
		openUnavailableServicesModal,
		removeServiceModal,
		validationResult
	])

	useEffect(() => {
		const customerNote = cartData?.cart.customerNote

		if (savedNote || !customerNote || hasInitializedNoteFromCart.current) {
			return
		}

		onInitNoteFromCart(customerNote)
		hasInitializedNoteFromCart.current = true
	}, [cartData?.cart.customerNote, onInitNoteFromCart, savedNote])

	const handleSubmitForm = async (values: ReservationBookingReservationsSummaryForm) => {
		const customerNote = values.note?.trim()
		const deviceID = cartReservationDeviceData?.deviceID

		if (!deviceID) {
			return
		}

		try {
			const validationResponse = await apiBrowser.b2c.getCartReservationsValidate(deviceID)

			if (validationResponse.validationResult) {
				setCartValidationResult(validationResponse.validationResult)
			}

			const unavailableItemIDs =
				validationResponse.validationResult?.cartReservationItems?.filter((item) => !item.isAvailable).map((item) => item.id) || []

			if (unavailableItemIDs.length > 0) {
				openUnavailableServicesModal(unavailableItemIDs)
				return
			}

			if (customerNote) {
				await apiBrowser.b2c.patchCart(deviceID, { customerNote })
			}
			onSubmitReservationsSummaryForm({
				note: customerNote || ''
			})
		} catch {
			// Error notifications are handled by the API layer.
		}
	}

	const handleUnavailableServicesModalConfirm = () => {
		onUnavailableServicesAcknowledged()
		setIsUnavailableServicesModalOpen(false)
		setUnavailableServicesModalDescription('')
		fetchCart()
	}

	const unknownButtonProps = {
		form: FORM.RESERVATION_BOOKING_MODAL_RESERVATIONS_SUMMARY
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} as any

	const handleRemoveServiceClick = (item: ReservationSummaryItem) => {
		setRemoveServiceModal({ item, isRemoving: false })
	}

	const handleRemoveServiceModalClose = () => {
		if (removeServiceModal?.isRemoving) {
			return
		}

		setRemoveServiceModal(null)
		fetchCart()
	}

	const handleRemoveServiceConfirm = async () => {
		const deviceID = cartReservationDeviceData?.deviceID
		const cartReservationItemID = removeServiceModal?.item.id

		if (!deviceID || !cartReservationItemID) {
			return
		}

		setRemoveServiceModal((current) => (current ? { ...current, isRemoving: true } : null))

		try {
			await apiBrowser.b2c.deleteCartReservation(deviceID, cartReservationItemID, { displayNotification: false })
			setRemoveServiceModal(null)
			await fetchCart()
		} catch {
			setRemoveServiceModal((current) => (current ? { ...current, isRemoving: false } : null))
		}
	}

	const handleCartExpired = useCallback(() => {
		setIsCartExpiredModalOpen(true)
	}, [])

	const handleCartExpiredConfirm = () => {
		setIsCartExpiredModalOpen(false)
		onCartExpired()
	}

	return (
		<ReservationBookingModalLayout
			title={formatMessage({ id: 'Booking summary', defaultMessage: 'Booking summary' })}
			headerImage={headerImage}
			onBackButtonClick={onRequestLeaveReservation}
			onClose={onRequestLeaveReservation}
			footerContent={
				<Button
					{...unknownButtonProps}
					type={'submit'}
					buttonStyle={ButtonModel.Styles.primary}
					fullWidth
					disabled={isFetchingCart || loadingError || isSubmitting || !areAllReservationItemsAvailable}
				>
					{messages.Continue}
				</Button>
			}
		>
			<CartReservationCountdown
				cartExpirationSeconds={cartExpirationSeconds}
				expiresInSeconds={expiresIn}
				progressState={progressState}
				onExpired={handleCartExpired}
				title={messages['Selected services']}
				description={formatMessage(
					{
						id: "We've reserved these services for you for {minutes} minutes while you finish your booking. You can reserve a maximum of {maxCountReservations} services.",
						defaultMessage:
							"We've reserved these services for you for {minutes} minutes while you finish your booking. You can reserve a maximum of {maxCountReservations} services."
					},
					{ minutes: cartExpirationMinutes, maxCountReservations: maxCountCartReservations }
				)}
				edgeOffset
			/>
			{(() => {
				if (loadingError) {
					return <ErrorScreen button={{ onClick: retryFetchCart }} />
				}
				return (
					<SC.Form id={FORM.RESERVATION_BOOKING_MODAL_RESERVATIONS_SUMMARY} onSubmit={handleSubmit(handleSubmitForm)}>
						<SC.Content>
							{isContentLoading ? (
								<ReservationSummaryListSkeleton />
							) : (
								<SC.List>
									{reservationItems.map((item) => {
										const isAvailable = validationAvailabilityByItemID.get(item.id)
										const isSameReservationDay = item.start.date === item.end.date
										const employeeName = getEmployeeName(item.employee)
										const timeSlotLabel = getCartReservationTimeSlotLabel(item)
										return (
											<SC.ListItemGroup key={item.id}>
												<SC.ListItem>
													<SC.ServiceImageWrapper>
														{item.service.previewImage?.resizedImages.thumbnail ? (
															<SC.ServiceImage
																$disabled={isAvailable === false}
																src={item.service.previewImage.resizedImages.thumbnail}
																alt={item.service.name || messages.Service}
															/>
														) : (
															<SC.ServiceImageFallback />
														)}
													</SC.ServiceImageWrapper>
													<SC.ListItemBody>
														<SC.ListItemHeader>
															<SC.ListItemMain $disabled={isAvailable === false}>
																<SC.ListItemTitle>{item.service.name || messages.Service}</SC.ListItemTitle>
																{isSameReservationDay ? (
																	<SC.MetaValue>{formatRelativeDate(item.start.date)}</SC.MetaValue>
																) : (
																	<>
																		<SC.MetaValue>
																			{formatMessage(
																				{ id: 'From {date} {time}', defaultMessage: 'From {date} {time}' },
																				{ date: formatRelativeDate(item.start.date), time: item.start.time }
																			)}
																		</SC.MetaValue>
																		<SC.MetaValue>
																			{formatMessage(
																				{ id: 'To {date} {time}', defaultMessage: 'To {date} {time}' },
																				{ date: formatRelativeDate(item.end.date), time: item.end.time }
																			)}
																		</SC.MetaValue>
																	</>
																)}
																{isSameReservationDay && <SC.MetaValue>{timeSlotLabel}</SC.MetaValue>}
																{employeeName && <SC.MetaValue>{employeeName}</SC.MetaValue>}
															</SC.ListItemMain>
															{/* // Remove service button */}
															{reservationItems.length > 1 && (
																<SC.ItemCloseButton
																	type={'button'}
																	aria-label={formatMessage({ id: 'Remove service', defaultMessage: 'Remove service' })}
																	onClick={() => handleRemoveServiceClick(item)}
																>
																	<IconRegularClose color={'icon.primary'} width={'1.25rem'} height={'1.25rem'} />
																</SC.ItemCloseButton>
															)}
														</SC.ListItemHeader>
														<SC.ItemFooter>
															<SC.ItemPrice
																$disabled={isAvailable === false}
																from={item.price.from}
																to={item.price.to}
																fromSecondary={item.price.fromSecondary}
																toSecondary={item.price.toSecondary}
															/>
															{/* // Edit service button */}
															<SC.ItemEditButton type={'button'} onClick={() => onBackToEmployeeAndDateSelection(item)}>
																{formatMessage({ id: 'Edit', defaultMessage: 'Edit' })}
															</SC.ItemEditButton>
														</SC.ItemFooter>
													</SC.ListItemBody>
												</SC.ListItem>
												{isAvailable === false && (
													<SC.UnavailableServicesInfoBanner>
														<InfoLine
															type={INFO_LINE_TYPE.INFO}
															message={formatMessage({
																id: 'Please select another time slot, change the employee or choose another service.',
																defaultMessage:
																	'Please select another time slot, change the employee or choose another service.'
															})}
														/>
													</SC.UnavailableServicesInfoBanner>
												)}
											</SC.ListItemGroup>
										)
									})}
								</SC.List>
							)}
							{/* // Add Another Service button */}
							{canAddAnotherService && (
								<SC.AddAnotherServiceButton
									type={'button'}
									buttonStyle={ButtonModel.Styles.secondary}
									fullWidth
									onClick={onAddAnotherService}
									disabled={isContentLoading}
								>
									+ {formatMessage({ id: 'Add another service', defaultMessage: 'Add another service' })}
								</SC.AddAnotherServiceButton>
							)}
							{/* // Add Note button */}
							<SC.NoteSection>
								<HookFormField
									control={control}
									name={'note'}
									component={TextAreaFormField}
									label={messages.Note}
									placeholder={messages['I would like to...']}
									maxLength={VALIDATION_MAX_LENGTH.LENGTH_1500}
									autoSize={{
										minRows: 1
									}}
								/>
							</SC.NoteSection>
						</SC.Content>
					</SC.Form>
				)
			})()}
			<ReservationBookingDialog
				isOpen={removeServiceModal !== null && !isCartExpiredModalOpen && !isLeaveReservationModalOpen}
				onClose={handleRemoveServiceModalClose}
				showHeaderCloseButton
				isHeaderCloseButtonDisabled={removeServiceModal?.isRemoving ?? false}
				title={messages['Remove service']}
				description={
					messages['The selected time slot will no longer be reserved for you. Are you sure you want to remove this service from the reservation?']
				}
				footer={
					<DialogSC.Footer>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.secondary}
								fullWidth
								disabled={removeServiceModal?.isRemoving ?? false}
								onClick={handleRemoveServiceModalClose}
							>
								{messages['No, keep']}
							</Button>
						</DialogSC.FooterButton>
						<DialogSC.FooterButton>
							<Button
								type={'button'}
								buttonStyle={ButtonModel.Styles.primary}
								fullWidth
								disabled={removeServiceModal?.isRemoving ?? false}
								aria-busy={removeServiceModal?.isRemoving ?? false}
								onClick={handleRemoveServiceConfirm}
							>
								{removeServiceModal?.isRemoving ? (
									<>
										<Spinner size={16} color={'icon.disabled'} aria-hidden />
										<VisuallyHidden>{messages['Yes, delete']}</VisuallyHidden>
									</>
								) : (
									messages['Yes, delete']
								)}
							</Button>
						</DialogSC.FooterButton>
					</DialogSC.Footer>
				}
			/>
			<ReservationBookingDialog
				isOpen={isCartExpiredModalOpen}
				onClose={handleCartExpiredConfirm}
				title={messages['Reservation time expired']}
				description={messages['The time to complete your reservation has expired. The selected time slots are no longer reserved for you.']}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleCartExpiredConfirm}>
						{messages.OK}
					</Button>
				}
			/>
			<ReservationBookingDialog
				isOpen={isUnavailableServicesModalOpen && !isCartExpiredModalOpen && !isLeaveReservationModalOpen && removeServiceModal === null}
				onClose={handleUnavailableServicesModalConfirm}
				title={formatMessage({ id: 'Service has been booked!', defaultMessage: 'Service has been booked!' })}
				description={unavailableServicesModalDescription}
				footer={
					<Button type={'button'} buttonStyle={ButtonModel.Styles.primary} fullWidth onClick={handleUnavailableServicesModalConfirm}>
						{messages.OK}
					</Button>
				}
			/>
		</ReservationBookingModalLayout>
	)
}

export default Step3ReservationsSummary
